import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import {
  MessageSquare, Send, Search, ArrowLeft, User, Clock, Loader2, 
  Inbox, ChevronRight, Check, CheckCheck
} from "lucide-react";

// Time ago helper
const timeAgo = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now - date) / 1000);
  
  if (seconds < 60) return "now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// Format timestamp for messages
const formatMessageTime = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
};

// Format date for message groups
const formatMessageDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  
  if (date.toDateString() === today.toDateString()) return "Today";
  if (date.toDateString() === yesterday.toDateString()) return "Yesterday";
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// Conversation List Item
const ConversationItem = ({ conversation, isActive, onClick }) => {
  const hasUnread = conversation.unread_count > 0;
  
  return (
    <button
      onClick={onClick}
      className={`w-full p-4 text-left border-b border-slate-100 hover:bg-slate-50 transition-colors ${
        isActive ? "bg-[#1e3a5f]/5 border-l-4 border-l-[#d4a853]" : ""
      }`}
      data-testid={`conversation-${conversation.participant_id}`}
    >
      <div className="flex items-start gap-3">
        {/* Avatar */}
        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
          hasUnread ? "bg-[#d4a853] text-white" : "bg-slate-200 text-slate-600"
        }`}>
          <User className="w-5 h-5" />
        </div>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1">
            <span className={`font-medium truncate ${hasUnread ? "text-[#1e3a5f]" : "text-slate-700"}`}>
              {conversation.participant_name}
            </span>
            <span className="text-xs text-slate-400 flex-shrink-0 ml-2">
              {timeAgo(conversation.last_timestamp)}
            </span>
          </div>
          <p className={`text-sm truncate ${hasUnread ? "text-slate-800 font-medium" : "text-slate-500"}`}>
            {conversation.last_message || "No messages yet"}
          </p>
        </div>
        
        {/* Unread badge */}
        {hasUnread && (
          <span className="w-5 h-5 rounded-full bg-[#d4a853] text-white text-xs flex items-center justify-center flex-shrink-0">
            {conversation.unread_count}
          </span>
        )}
      </div>
    </button>
  );
};

// Message Bubble
const MessageBubble = ({ message, isOwn, showTimestamp }) => {
  return (
    <div className={`flex ${isOwn ? "justify-end" : "justify-start"} mb-2`}>
      <div className={`max-w-[75%] ${isOwn ? "order-2" : "order-1"}`}>
        <div className={`rounded-2xl px-4 py-2.5 ${
          isOwn 
            ? "bg-[#1e3a5f] text-white rounded-br-md" 
            : "bg-slate-100 text-slate-800 rounded-bl-md"
        }`}>
          <p className="text-sm whitespace-pre-wrap break-words">{message.message_text}</p>
        </div>
        {showTimestamp && (
          <div className={`flex items-center gap-1 mt-1 ${isOwn ? "justify-end" : "justify-start"}`}>
            <span className="text-xs text-slate-400">{formatMessageTime(message.timestamp)}</span>
            {isOwn && (
              message.read 
                ? <CheckCheck className="w-3 h-3 text-[#d4a853]" />
                : <Check className="w-3 h-3 text-slate-400" />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Empty State
const EmptyState = ({ type }) => {
  const content = {
    noConversations: {
      icon: Inbox,
      title: "No conversations yet",
      description: "Messages from customers will appear here. Accept leads to start conversations."
    },
    selectConversation: {
      icon: MessageSquare,
      title: "Select a conversation",
      description: "Choose a conversation from the list to view messages."
    }
  };
  
  const { icon: Icon, title, description } = content[type] || content.noConversations;
  
  return (
    <div className="h-full flex items-center justify-center p-8">
      <div className="text-center">
        <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
          <Icon className="w-8 h-8 text-slate-400" />
        </div>
        <h3 className="text-lg font-semibold text-[#1e3a5f] mb-2">{title}</h3>
        <p className="text-slate-500 max-w-sm">{description}</p>
      </div>
    </div>
  );
};

// Main Conversations Page
export default function ProConversationsPage() {
  const { user, token } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [participant, setParticipant] = useState(null);
  const [newMessage, setNewMessage] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [showMobileList, setShowMobileList] = useState(true);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Fetch conversations
  const fetchConversations = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await axios.get(`${API}/messages/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(response.data.conversations || []);
    } catch (error) {
      console.error("Failed to fetch conversations:", error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchConversations();
    
    // Poll for new messages every 30 seconds
    const interval = setInterval(fetchConversations, 30000);
    return () => clearInterval(interval);
  }, [fetchConversations]);

  // Fetch messages for active conversation
  const fetchMessages = useCallback(async (participantId) => {
    if (!token || !participantId) return;
    
    setMessagesLoading(true);
    try {
      const response = await axios.get(`${API}/messages/conversation/${participantId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data.messages || []);
      setParticipant(response.data.participant);
      
      // Update unread count in conversations list
      setConversations(prev => prev.map(conv => 
        conv.participant_id === participantId 
          ? { ...conv, unread_count: 0 }
          : conv
      ));
    } catch (error) {
      console.error("Failed to fetch messages:", error);
      toast.error("Failed to load messages");
    } finally {
      setMessagesLoading(false);
    }
  }, [token]);

  // Select a conversation
  const handleSelectConversation = (conversation) => {
    setActiveConversation(conversation.participant_id);
    fetchMessages(conversation.participant_id);
    setShowMobileList(false);
  };

  // Send a message
  const handleSendMessage = async () => {
    if (!newMessage.trim() || !activeConversation || sending) return;
    
    setSending(true);
    const messageText = newMessage.trim();
    setNewMessage("");
    
    // Optimistically add message to UI
    const optimisticMessage = {
      id: `temp-${Date.now()}`,
      sender_id: user.id,
      sender_type: user.user_type,
      receiver_id: activeConversation,
      message_text: messageText,
      timestamp: new Date().toISOString(),
      read: false
    };
    setMessages(prev => [...prev, optimisticMessage]);
    
    try {
      const response = await axios.post(
        `${API}/messages/send`,
        { receiver_id: activeConversation, message_text: messageText },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Replace optimistic message with real one
      setMessages(prev => prev.map(msg => 
        msg.id === optimisticMessage.id ? response.data.data : msg
      ));
      
      // Update conversation list
      fetchConversations();
    } catch (error) {
      // Remove optimistic message on error
      setMessages(prev => prev.filter(msg => msg.id !== optimisticMessage.id));
      toast.error("Failed to send message");
      setNewMessage(messageText);
    } finally {
      setSending(false);
    }
  };

  // Handle Enter key
  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Filter conversations by search
  const filteredConversations = conversations.filter(conv =>
    conv.participant_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Group messages by date
  const groupedMessages = messages.reduce((groups, message) => {
    const date = formatMessageDate(message.timestamp);
    if (!groups[date]) groups[date] = [];
    groups[date].push(message);
    return groups;
  }, {});

  return (
    <div className="h-[calc(100vh-8rem)] flex bg-white rounded-xl shadow-sm overflow-hidden" data-testid="pro-conversations-page">
      {/* Conversation List - Left Panel */}
      <div className={`w-full md:w-80 lg:w-96 border-r border-slate-200 flex flex-col ${
        !showMobileList && activeConversation ? "hidden md:flex" : "flex"
      }`}>
        {/* Search Header */}
        <div className="p-4 border-b border-slate-100">
          <h2 className="text-lg font-bold text-[#1e3a5f] mb-3">Conversations</h2>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search conversations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 border-slate-200"
              data-testid="conversations-search"
            />
          </div>
        </div>
        
        {/* Conversations List */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-8 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-[#d4a853] mx-auto" />
            </div>
          ) : filteredConversations.length === 0 ? (
            <EmptyState type="noConversations" />
          ) : (
            filteredConversations.map((conv) => (
              <ConversationItem
                key={conv.participant_id}
                conversation={conv}
                isActive={activeConversation === conv.participant_id}
                onClick={() => handleSelectConversation(conv)}
              />
            ))
          )}
        </div>
      </div>

      {/* Messages Panel - Right Panel */}
      <div className={`flex-1 flex flex-col ${
        showMobileList && !activeConversation ? "hidden md:flex" : "flex"
      }`}>
        {!activeConversation ? (
          <EmptyState type="selectConversation" />
        ) : (
          <>
            {/* Conversation Header */}
            <div className="p-4 border-b border-slate-100 flex items-center gap-3">
              <button
                onClick={() => setShowMobileList(true)}
                className="md:hidden p-2 hover:bg-slate-100 rounded-lg"
              >
                <ArrowLeft className="w-5 h-5 text-slate-600" />
              </button>
              
              <div className="w-10 h-10 rounded-full bg-[#1e3a5f]/10 flex items-center justify-center">
                <User className="w-5 h-5 text-[#1e3a5f]" />
              </div>
              
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-[#1e3a5f] truncate">
                  {participant?.name || "Customer"}
                </h3>
                <p className="text-xs text-slate-500 capitalize">{participant?.type || "customer"}</p>
              </div>
            </div>

            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto p-4 bg-slate-50">
              {messagesLoading ? (
                <div className="h-full flex items-center justify-center">
                  <Loader2 className="w-8 h-8 animate-spin text-[#d4a853]" />
                </div>
              ) : messages.length === 0 ? (
                <div className="h-full flex items-center justify-center text-center">
                  <div>
                    <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">No messages yet. Start the conversation!</p>
                  </div>
                </div>
              ) : (
                Object.entries(groupedMessages).map(([date, dateMessages]) => (
                  <div key={date}>
                    {/* Date Separator */}
                    <div className="flex items-center justify-center my-4">
                      <span className="px-3 py-1 bg-white rounded-full text-xs text-slate-500 shadow-sm">
                        {date}
                      </span>
                    </div>
                    
                    {/* Messages */}
                    {dateMessages.map((message, index) => (
                      <MessageBubble
                        key={message.id}
                        message={message}
                        isOwn={message.sender_id === user?.id}
                        showTimestamp={
                          index === dateMessages.length - 1 ||
                          dateMessages[index + 1]?.sender_id !== message.sender_id
                        }
                      />
                    ))}
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Message Input */}
            <div className="p-4 border-t border-slate-100 bg-white">
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <Input
                    ref={inputRef}
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Type a message..."
                    className="border-slate-200 focus:border-[#1e3a5f]"
                    disabled={sending}
                    data-testid="message-input"
                  />
                </div>
                <Button
                  onClick={handleSendMessage}
                  disabled={!newMessage.trim() || sending}
                  className="bg-[#1e3a5f] hover:bg-[#152a45] h-10 px-4"
                  data-testid="send-message-btn"
                >
                  {sending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
